import { KeyboardEvent, useEffect, useState } from 'react';
import { Button, Col, Divider, Form, message, Space, Typography } from 'antd';
import Title from 'antd/lib/typography/Title';
import { CalendarOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from "react-router-dom";
import { EventService } from '../../Services/EventService';
import { useUpdateEvent } from '../../Hooks/useUpdateEvent';
import moment from 'moment';
import 'moment-timezone'
import { EventEditState } from '../../Pages/Event/EventState';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { BookyCustomInput } from '../../Components/Forms/BookyCustomInput';
import { BookyTitleWithIcon } from '../../Components/Forms/TitleWithIcon';
import { EventDetailsFields, EventSettingsFields, EventTimesFields } from '../../Components/Forms/EventFormSections';
import { useAuth } from '../../Contexts/AuthContext';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import PageHelp from '../../Components/Page/PageHelp';
import { EventHeader } from '../../Components/EventDetails/EventHeader';
import LeftMenuEventDetails from './LeftMenuEventDetails';
import { EventType } from '../../Types/EventType';
import { AnswerOptions } from '../../Types/AnswerOptions';
import { TimeChoice } from '../../Types/TimeChoice';
import { getDeadlineError, getFirstStart } from '../../Helpers/EventDateValidation';

// "Ändra event": the same sections in the same order as Skapa event (EventFormSections), so creating
// and changing an event look alike. Saving stays on the page.
const MyEventEdit = () => {
  const [event, setEvent] = useState([] as any);
  const { events, step, changeStep, CreateEventItem, UpdateEvent, DeleteEvent, EditEvent, timeZone, setTimeZone, LoadTimeZones, timeZones, SetFullDayOnEvent, setMaxParticipants, maxParticipants, allowWaitlist, setAllowWaitlist, maxWaitlist, setMaxWaitlist, membersOnly, setMembersOnly, showOnMemberSite, setShowOnMemberSite, eventType, setEventType, timeChoice, setTimeChoice, setAutomaticReminder, automaticReminder, automaticReminderDays, setAutomaticReminderDays, setDeadline, deadline, displayType, setDisplayType, AddEvents, eventState, setEventState, customStep, setCustomStep } = useUpdateEvent();
  const [form] = Form.useForm();
  const [show24hrsFormat, setShow24hrsFormat] = useState(false as boolean);
  // Most guests holding a place on any one time - the max can't go below it once there are replies.
  const [mostBooked, setMostBooked] = useState(0);
  const [startDate, setStartDate] = useState("" as string);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();

  const GetText = TextService.GetText;
  const params = useParams();
  const navigation = useNavigate();
  const isPoll = eventType === EventType.Poll;
  const isPremium = user?.subscriptionLevel !== SubscriptionLevel.Free;

  // A saved deadline that has passed may stay, so a closed event can still be saved. A changed one must be ahead.
  const [savedDeadline, setSavedDeadline] = useState("");
  const deadlineError = getDeadlineError(deadline, events, deadline !== savedDeadline);

  const FormSaveClick = async (formValues: any) => {
    if (deadlineError) {
      message.error(deadlineError);
      return;
    }
    setIsSaving(true);
    try {
      await EditEvent(event.eventId, formValues);
    }
    catch {
      message.error(GetText("MyEventsEdit_SaveFailed"));
      return;
    }
    finally {
      setIsSaving(false);
    }
    // Stay on the page, like everywhere else in admin.
    message.success(GetText("Common_Saved"));
  }

  // The answer options of a poll and a sign-up are different, so a changed type starts with its own default.
  // Once there are replies the options are locked and kept as they are.
  const onEventTypeChange = (value: EventType) => {
    setEventType(value);
    if (eventState !== EventEditState.HasBookings) {
      form.setFieldValue("answerOptions", value === EventType.Poll ? AnswerOptions.YesNoMaybe : AnswerOptions.YesNo);
    }
  }

  const OnCancelClick = () => {
    navigation("/" + getCurrentLanguage() + "/myevents/" + params.id);
  }

  // Enter in a field must not save the event, e.g. when choosing a place in the location field.
  const onFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    const target = e.target as HTMLElement;
    if (e.key === "Enter" && target.tagName !== "TEXTAREA" && target.tagName !== "BUTTON") {
      e.preventDefault();
    }
  }

  // Once there are replies the max may not take a place away from someone who already has one.
  const minParticipants = eventState === EventEditState.HasBookings ? Math.max(1, mostBooked) : 1;

  const onNavigate = (date: any) => {
    setStartDate(moment(date).startOf('week').toISOString());
  }

  useEffect(() => {
    if (params.id != null) {

      EventService.GetEventDetails(params.id).then(result => {
        form.setFieldsValue({
          name: result.eventName,
          eventDescription: result.eventDescription,
          eventLocation: result.eventLocation,
        });

        AddEvents(result.times);

        // The calendar opens on the week of the first time.
        const firstStart = (result.times ?? []).map(time => new Date(time.start!).getTime()).sort((a, b) => a - b)[0];
        if (firstStart) {
          setStartDate(moment(firstStart).startOf('week').toISOString());
        }

        if (result.eventDeadline) {
          setDeadline(result.eventDeadline.toString());
          setSavedDeadline(result.eventDeadline.toString());
        }

        if (result.automaticReminder) {
          setAutomaticReminder(true);
          setAutomaticReminderDays(result.automaticReminderDays ?? 2);
        }

        if (result.eventTimeZoneId) {
          LoadTimeZones(result.eventTimeZoneId);
          moment.tz.setDefault(result.eventTimeZoneId);
        }

        if (result.eventDisplayType) {
          setDisplayType(result.eventDisplayType);
        }

        if (result.eventParticipants) {
          setMaxParticipants(result.eventParticipants);
        }

        setAllowWaitlist(result.allowWaitlist === true);
        setMaxWaitlist(result.maxWaitlist ?? null);
        setMembersOnly(result.membersOnly === true);
        setShowOnMemberSite(result.showOnMemberSite === true);
        setEventType(result.eventType === EventType.Poll ? EventType.Poll : EventType.SignUp);
        // Older sign-ups have yes/no/maybe saved but are answered "Kommer" / "Kommer inte".
        form.setFieldValue("answerOptions", result.eventType !== EventType.Poll && result.answerOptions !== AnswerOptions.Yes
          ? AnswerOptions.YesNo : result.answerOptions);
        setTimeChoice(result.timeChoice === TimeChoice.One ? TimeChoice.One : TimeChoice.Several);

        if (result.timeAnswers != null && result.timeAnswers.length > 0) {
          setEventState(EventEditState.HasBookings);
        }
        setMostBooked(Math.max(0, ...(result.times ?? []).map(time => time.yesCount ?? 0)));

        setEvent(result);

        if (getCurrentLanguage() === "se") {
          setShow24hrsFormat(true);
        }
      });
    }
  }, [])

  const boxStyle = { marginBottom: "20px", textAlign: "left" as const };

  return <LayoutLeftMenu hasTitleTop={true} header={<EventHeader event={event} />} fullpageMain={true} title={GetText("MyEventsEdit_UpdateEvent")} menu={<LeftMenuEventDetails selected={"editevent"} membersOnly={event.membersOnly} />}>
    <Col xs={24} md={18} lg={16}>
      <Form
        data-testid="create-form"
        name="basic"
        form={form}
        onFinish={FormSaveClick}
        onKeyDown={onFormKeyDown}
        layout="vertical"
        autoComplete="off"
      >
        <Col className='bookyBox event-page-box' style={boxStyle}>
          <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
            <Title style={{ marginTop: 0 }}>{GetText("MyEventsEdit_UpdateEvent")}</Title>
            <PageHelp>{GetText("MyEventsEdit_HowItWorks").split("\n\n").map((text, i) => <Typography.Paragraph key={i}>{text}</Typography.Paragraph>)}</PageHelp>
          </Space>
          <Divider className="BookyTitleWithIconDivider" />
          <EventDetailsFields eventType={eventType} onEventTypeChange={onEventTypeChange} defaultLocation={event.eventLocation}
            onLocationSelected={(place: any) => { form.setFieldValue("eventLocation", place) }} />
        </Col>

        <Col className='bookyBox event-page-box' style={boxStyle}>
          {eventState === EventEditState.Found ?
            <EventTimesFields step={step} onStepChange={changeStep} customStep={customStep} onCustomStepChange={setCustomStep}
              calendarEvents={events} times={events} startDate={startDate} onNavigate={onNavigate}
              CreateEventItem={CreateEventItem} UpdateEvent={UpdateEvent} DeleteEvent={DeleteEvent} SetFullDayOnEvent={SetFullDayOnEvent}
              show24hrsFormat={show24hrsFormat} timeZone={timeZone} timeZones={timeZones} onTimeZoneChange={setTimeZone} /> :
            <>
              {/* Someone has replied, so the times can no longer be changed. */}
              <BookyTitleWithIcon text={GetText("Create_Times")}>
                <CalendarOutlined />
              </BookyTitleWithIcon>
              <Typography><b>{GetText("MyEventsEdit_HasGuestsLockedChanges")}</b></Typography>
            </>}
        </Col>

        <Col className='bookyBox event-page-box' style={boxStyle}>
          <EventSettingsFields isPoll={isPoll} isPremium={isPremium}
            onAnswerOptionsChange={(value) => form.setFieldValue("answerOptions", value)}
            answerOptionsLocked={eventState === EventEditState.HasBookings}
            deadline={deadline} onDeadlineChange={setDeadline} deadlineError={deadlineError} firstStart={getFirstStart(events)}
            maxParticipants={maxParticipants} onMaxParticipantsChange={setMaxParticipants} minParticipants={minParticipants}
            membersOnly={membersOnly} setMembersOnly={setMembersOnly} showOnMemberSite={showOnMemberSite} setShowOnMemberSite={setShowOnMemberSite}
            allowWaitlist={allowWaitlist} setAllowWaitlist={setAllowWaitlist} maxWaitlist={maxWaitlist} setMaxWaitlist={setMaxWaitlist}
            timeChoice={timeChoice} setTimeChoice={setTimeChoice} timesCount={events.length}
            displayType={displayType} onDisplayTypeChange={setDisplayType}
            automaticReminder={automaticReminder} setAutomaticReminder={setAutomaticReminder}
            automaticReminderDays={automaticReminderDays} setAutomaticReminderDays={setAutomaticReminderDays} />

          <BookyCustomInput>
            <Space>
              <Button size="middle" type="default" onClick={OnCancelClick}>{GetText("Common_Cancel")}</Button>
              <Button data-testid="submit-button" size="middle" type="primary" htmlType="submit" loading={isSaving}>
                {GetText("MyEventsEdit_UpdateEventButton")}
              </Button>
            </Space>
          </BookyCustomInput>
        </Col>
      </Form>
    </Col>
  </LayoutLeftMenu >
};

export default MyEventEdit;
